const EXAM_TYPE_MAP = {
  kennistentamen: 'kennistentamen',
  'onderzoeksverslag/ voorstel': 'onderzoeksverslag-voorstel',
  'onderzoeksverslag/voorstel': 'onderzoeksverslag-voorstel',
  beroepsproduct: 'beroepsproduct',
  adviesrapport: 'adviesrapport',
  'reflectieverslag/ portfolio': 'reflectieverslag-portfolio',
  'reflectieverslag/portfolio': 'reflectieverslag-portfolio',
  reflectieverslag: 'reflectieverslag-portfolio',
  'schriftelijke opdracht': 'schriftelijke-opdracht',
  'schriftelijke-opdracht': 'schriftelijke-opdracht',
  presentatievorm: 'presentatievorm'
};

const PREFIX_EXAM_MAP = {
  reflectieverslag: 'reflectieverslag-portfolio',
  onderzoeksverslag: 'onderzoeksverslag-voorstel',
  schriftelijke: 'schriftelijke-opdracht',
  adviesrapport: 'adviesrapport',
  beroepsproduct: 'beroepsproduct',
  kennistentamen: 'kennistentamen',
  presentatievorm: 'presentatievorm'
};

export const normalizeExamType = (name = '') => {
  const normalized = String(name || '').trim().toLowerCase().replace(/\s*\/\s*/g, '/ ');
  if (EXAM_TYPE_MAP[normalized]) return EXAM_TYPE_MAP[normalized];

  const slashNorm = normalized.replace(/\s*\/\s*/g, '/');
  for (const [key, value] of Object.entries(EXAM_TYPE_MAP)) {
    if (key.replace(/\s*\/\s*/g, '/') === slashNorm) return value;
  }

  for (const [prefix, mapped] of Object.entries(PREFIX_EXAM_MAP)) {
    if (normalized.startsWith(prefix)) return mapped;
  }

  return normalized.replace(/[^\w]+/g, '-').replace(/^-|-$/g, '') || 'other';
};

const toNumericGrade = (gradeValue) => {
  const numeric = Number(gradeValue);
  if (Number.isNaN(numeric)) return null;
  if (numeric < 0 || numeric > 10) return null;
  return numeric;
};

const buildGradeByExamType = (grades = []) => {
  const map = new Map();

  (grades || []).forEach((grade) => {
    const numericGrade = toNumericGrade(grade?.grade);
    if (numericGrade === null) return;

    const rawExamType = String(grade?.examType || '').trim();
    const fallbackExamType = String(grade?.notes || '').trim();
    const examType = normalizeExamType(rawExamType || fallbackExamType || 'other');

    map.set(examType, numericGrade);
  });

  return map;
};

const buildGradeByExamPointKey = (grades = []) => {
  const map = new Map();

  (grades || []).forEach((grade) => {
    const numericGrade = toNumericGrade(grade?.grade);
    if (numericGrade === null) return;

    const examPointKey = String(grade?.examPointKey || '').trim();
    if (!examPointKey) return;
    map.set(examPointKey, numericGrade);
  });

  return map;
};

export const floorToOneDecimal = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.floor((numeric + Number.EPSILON) * 10) / 10;
};

export const formatGradeOneDecimal = (value) => {
  const floored = floorToOneDecimal(value);
  if (floored === null) return '-';
  return floored.toFixed(1);
};

export const calculateWeightedFinalGrade = ({ grades = [], examPoints = [] } = {}) => {
  const validGrades = (grades || []).filter((g) => toNumericGrade(g?.grade) !== null);
  if (validGrades.length === 0) {
    return {
      finalGrade: null,
      isComplete: false,
      usedWeight: 0,
      totalWeight: 0
    };
  }

  const validExamPoints = (examPoints || [])
    .map((point, index) => ({
      ...point,
      examPointKey: String(point?.id || point?._id || `${normalizeExamType(point?.name || point?.examType || 'other')}-${index}`),
      examType: normalizeExamType(point?.name || point?.examType || 'other'),
      weight: Number(point?.weight || 0)
    }))
    .filter((point) => !Number.isNaN(point.weight) && point.weight > 0);

  if (validExamPoints.length > 0) {
    const gradeByExamType = buildGradeByExamType(validGrades);
    const gradeByExamPointKey = buildGradeByExamPointKey(validGrades);
    const totalWeight = validExamPoints.reduce((sum, point) => sum + point.weight, 0);

    let usedWeight = 0;
    let weightedSum = 0;

    validExamPoints.forEach((point) => {
      const grade = gradeByExamPointKey.has(point.examPointKey)
        ? gradeByExamPointKey.get(point.examPointKey)
        : gradeByExamType.get(point.examType);
      if (typeof grade === 'number') {
        weightedSum += grade * point.weight;
        usedWeight += point.weight;
      }
    });

    if (usedWeight === 0) {
      return {
        finalGrade: null,
        isComplete: false,
        usedWeight: 0,
        totalWeight
      };
    }

    return {
      finalGrade: weightedSum / usedWeight,
      isComplete: usedWeight === totalWeight,
      usedWeight,
      totalWeight
    };
  }

  const weightedEntries = validGrades
    .map((grade) => {
      const weight = Number(grade?.weight || 0);
      return {
        grade: Number(grade.grade),
        weight: Number.isNaN(weight) || weight <= 0 ? 1 : weight
      };
    });

  const totalWeight = weightedEntries.reduce((sum, entry) => sum + entry.weight, 0);
  if (totalWeight <= 0) {
    return {
      finalGrade: null,
      isComplete: false,
      usedWeight: 0,
      totalWeight: 0
    };
  }

  const weightedSum = weightedEntries.reduce((sum, entry) => sum + entry.grade * entry.weight, 0);
  return {
    finalGrade: weightedSum / totalWeight,
    isComplete: true,
    usedWeight: totalWeight,
    totalWeight
  };
};
